import React, {
  useContext,
  useEffect,
  useState,
  Suspense,
  useRef,
  useMemo,
  useCallback,
} from "react";
import styled from "styled-components";
import {
  List,
  FlexboxGrid,
  Loader,
  Message,
  Badge,
  Modal,
  Whisper,
  Popover,
  Toggle,
  Checkbox,
  Tooltip,
} from "rsuite";
import { GlassCard } from "../../../../ui";
import { colors } from "../../../../ui/theme/colors";
import { shadows } from "../../../../ui/theme/shadows";
import "rsuite/dist/rsuite.min.css";
import { DarkModeContext } from "../../../../../appContexts/DarkModeContext";
import { useHistory } from "react-router-dom";
import { renderCount } from "../../../../../appHelpers/appLoggers";
import LocationIcon from "@rsuite/icons/Location";
import { LastReadFC } from "./_helpers/dashboardWidgets/LastReadFC";
import { RecentValuesFC } from "./_helpers/dashboardWidgets/RecentValuesFC";
import { appTheme } from "../../../../../appHelpers/appTheme";
import { _FilterEquipmentFC } from "./_helpers/dashboardFilters/_FilterEquipmentFC";
import { MachinesContext } from "./_context/MachinesContext";
import RemindFillIcon from "@rsuite/icons/RemindFill";
import NoticeIcon from "@rsuite/icons/Notice";
import { DateTime, Interval } from "luxon";
import { UserContext } from "../../_context/UserContext";
import { AlertListFC } from "./_helpers/AlertListFC";

export const TableEDU = ({
  machines,
  alertConfigs,
  networkIssues,
  dashboardAlerts,
}) => {
  // renderCount('MachinesTableFC');
  const { user, userSiteGroups } = useContext(UserContext);
  const history = useHistory();
  const [eduMachines, setEDUMachines] = useState(
    machines.filter((system) => system.process_edu === true),
  );
  const [displayMachines, setDisplayMachines] = useState(
    machines.filter((system) => system.process_edu === true),
  );
  const [displayCustomers, setDisplayCustomers] = useState([
    ...new Set(
      machines
        .filter((system) => system.process_edu === true)
        .map((customer) => customer.customer_name),
    ),
  ]);

  const handleSiteClick = (siteName, customerName) => {
    // Navigate to a site-specific view - you can customize this URL pattern
    history.push(
      `/customer/${encodeURIComponent(customerName)}/site/${encodeURIComponent(siteName)}`,
    );
  };

  // Memoize grouped systems to avoid recalculating on every render
  const groupedSystemsByCustomer = useMemo(() => {
    const cache = {};

    displayCustomers.forEach((customerName) => {
      const customerSystems = displayMachines?.filter(
        (item) => item.customer_name === customerName,
      );
      const result = [];

      // Get all site groups the user has access to for this customer
      const userCustomerSiteGroups =
        userSiteGroups?.filter(
          (userGroup) => userGroup.customer_name === customerName,
        ) || [];

      // Create set of sites that are in user's AUTHORIZED site groups
      const sitesInAuthorizedGroups = new Set();

      // Only process if user has explicit site group access
      if (userCustomerSiteGroups.length > 0) {
        // Group user's site groups and their systems
        const siteGroupMap = {};
        userCustomerSiteGroups.forEach((userGroup) => {
          if (
            userGroup.site_group_name &&
            !siteGroupMap[userGroup.site_group_name]
          ) {
            siteGroupMap[userGroup.site_group_name] = new Set();
          }
          if (userGroup.site_name && userGroup.site_group_name) {
            siteGroupMap[userGroup.site_group_name].add(userGroup.site_name);
            sitesInAuthorizedGroups.add(userGroup.site_name);
          }
        });

        // Add systems grouped by authorized site groups first
        Object.entries(siteGroupMap).forEach(([groupName, sites]) => {
          const groupSystems = customerSystems.filter((system) =>
            sites.has(system.site_name),
          );
          if (groupSystems.length > 0) {
            result.push({
              type: "group",
              site_group_name: groupName,
              systems: groupSystems,
            });
          }
        });
      }

      // Get individual systems (sites user has access to but NOT through a site group)
      const individualSystems = customerSystems.filter(
        (system) => !sitesInAuthorizedGroups.has(system.site_name),
      );

      // Add individual systems last
      if (individualSystems.length > 0) {
        result.push({
          type: "individual",
          systems: individualSystems,
        });
      }

      cache[customerName] = result;
    });

    return cache;
  }, [displayMachines, displayCustomers, userSiteGroups]);

  const getGroupedSystemsForCustomer = useCallback(
    (customerName) => {
      return groupedSystemsByCustomer[customerName] || [];
    },
    [groupedSystemsByCustomer],
  );
  const [displayFileDatetimes, setDisplayFileDatetimes] = useState([]);
  const { darkMode } = useContext(DarkModeContext);
  const clickStartRef = useRef(null);

  const speakerNetworkIssues = (
    <Tooltip>Warning: This system has an unresolved reported issue.</Tooltip>
  );

  const speakerElement = (system) => (
    <Popover style={{ width: "600px" }}>
      <AlertListFC user={user} system={system} />
    </Popover>
  );

  const {
    loadingDisplayRecentEDUValues,
    setLoadingDisplaySparklineData,
    displayRecentEDUValues,
    getRecentEDUValues,
    getDisplayRecentEDUValuesError,
    frozenSystems,
  } = useContext(MachinesContext);

  useEffect(() => {
    getRecentEDUValues();
  }, [machines]);

  return displayMachines ? (
    <MachineTableSC darkMode={darkMode}>
      {/* Filter Control Header Row */}
      <_FilterEquipmentFC
        machines={eduMachines}
        displayMachines={displayMachines}
        setDisplayMachines={setDisplayMachines}
        recent_data={displayRecentEDUValues}
        alertConfigs={alertConfigs}
        networkIssues={networkIssues}
        frozenSystems={frozenSystems}
        dashboardAlerts={dashboardAlerts}
      />

      {window.screen.width >= 768 ? (
        <List size="sm" hover>
          {/* Header For Equipment List */}
          {displayCustomers.map((customer, customerIndex) => (
            <CustomerCard
              key={customer}
              darkMode={darkMode}
              style={{ marginTop: customerIndex === 0 ? "16px" : undefined }}
            >
              {/* Customer Header */}
              <CustomerHeader>
                <CustomerText> {customer} </CustomerText>
              </CustomerHeader>

              {customerIndex === 0 ? <RowHeader /> : <></>}

              {(() => {
                const groupedSystems = getGroupedSystemsForCustomer(customer);
                const hasGroups = groupedSystems.some(
                  (group) => group.type === "group",
                );

                return groupedSystems.flatMap((group, groupIndex) => [
                  // Add header for site groups and individual sites (only show Individual Sites header if there are also groups)
                  group.type === "group" ||
                  (group.type === "individual" && hasGroups) ? (
                    <SiteGroupHeader
                      key={`${customer}-${group.type}-${group.site_group_name || "individual"}`}
                    >
                      <SiteGroupText darkMode={darkMode}>
                        {group.type === "group"
                          ? `${group.site_group_name} (${group.systems.length})`
                          : `Individual Sites (${group.systems.length})`}
                      </SiteGroupText>
                    </SiteGroupHeader>
                  ) : null,
                  // Add all systems in this group
                  ...group.systems.map((system) => (
                    <List.Item key={system.id + "_edu"}>
                      <SystemRow
                        onMouseDown={(event) => {
                          clickStartRef.current = event.timeStamp; // Record the timestamp of the mouse down event
                        }}
                        onMouseUp={(event) => {
                          const clickEnd = event.timeStamp; // Get the timestamp of the mouse up event
                          const clickDuration =
                            clickEnd - (clickStartRef.current || 0); // Calculate the duration of the click
                          const modalElement =
                            document.getElementById("control-id-hover");

                          if (
                            clickDuration < 200 &&
                            !modalElement?.contains(event.target)
                          ) {
                            history.push(`/system/${system.id}`); // Navigate to the next page if the click was short
                          }
                        }}
                      >
                        <FlexboxGrid>
                          <FlexboxGrid.Item
                            align="center"
                            colspan={1}
                            style={{ alignSelf: "center" }}
                          >
                            {loadingDisplayRecentEDUValues ? (
                              <Loader speed="fast" />
                            ) : (
                              <div style={{ display: "flex" }}>
                                <div
                                  style={{
                                    width: "50%",
                                    textAlign: "center",
                                    alignSelf: "center",
                                  }}
                                >
                                  {displayRecentEDUValues
                                    ?.filter((edu) => edu.id === system.id)
                                    .map((data) => (
                                      <VersionText>{data.version}</VersionText>
                                    ))}
                                </div>
                                <div
                                  style={{
                                    width: "40%",
                                    textAlign: "center",
                                    alignSelf: "center",
                                  }}
                                >
                                  {networkIssues ? (
                                    networkIssues[0]?.filter(
                                      (issue) =>
                                        issue.id === system.id &&
                                        !issue.resolved &&
                                        issue.status === "active",
                                    ).length > 0 ? (
                                      <Whisper
                                        placement="bottom"
                                        controlId="control-id-hover-alert"
                                        trigger="hover"
                                        speaker={speakerNetworkIssues}
                                      >
                                        <RemindFillIcon color="#ffb300" />
                                      </Whisper>
                                    ) : null
                                  ) : null}
                                </div>
                              </div>
                            )}
                          </FlexboxGrid.Item>
                          <FlexboxGrid.Item
                            colspan={2}
                            style={{ alignSelf: "center" }}
                          >
                            <ItemMainText>{system.id}</ItemMainText>
                            <ItemSubText>{system.cus_sys_id}</ItemSubText>
                          </FlexboxGrid.Item>
                          <FlexboxGrid.Item
                            colspan={3}
                            style={{ alignSelf: "center" }}
                          >
                            <ItemMainText>{system.manufacturer}</ItemMainText>
                            <ItemSubText>
                              {system.modality} | {system.model}
                            </ItemSubText>
                          </FlexboxGrid.Item>
                          <FlexboxGrid.Item
                            colspan={4}
                            style={{ alignSelf: "center" }}
                          >
                            <ItemMainText>{system.site_name}</ItemMainText>
                            <ItemSubText>
                              {system.state} · {system.city}{" "}
                            </ItemSubText>
                          </FlexboxGrid.Item>
                          <FlexboxGrid.Item
                            colspan={1}
                            align="center"
                            style={{ alignSelf: "center" }}
                          >
                            {alertConfigs?.filter(
                              (config) =>
                                config.system_id === system.id &&
                                config.user_id.toLowerCase() ===
                                  user?.email_address.toLowerCase() &&
                                config.pg_table.includes("edu") &&
                                config.enabled,
                            ).length !== 0 ? (
                              <Whisper
                                preventOverflow
                                placement="left"
                                trigger="hover"
                                controlId="control-id-hover"
                                speaker={speakerElement(system)}
                                enterable
                              >
                                <NoticeIcon color={appTheme.avante_dark_blue} />
                              </Whisper>
                            ) : alertConfigs?.filter(
                                (config) =>
                                  config.system_id === system.id &&
                                  config.user_id.toLowerCase() ===
                                    user?.email_address.toLowerCase() &&
                                  config.pg_table.includes("edu"),
                              ).length !== 0 ? (
                              <Whisper
                                preventOverflow
                                placement="left"
                                trigger="hover"
                                controlId="control-id-hover"
                                speaker={speakerElement(system)}
                                enterable
                              >
                                <NoticeIcon color="grey" />
                              </Whisper>
                            ) : (
                              <NoticeIcon color="red" />
                            )}
                          </FlexboxGrid.Item>
                          {loadingDisplayRecentEDUValues ? (
                            <>
                              <FlexboxGrid.Item
                                colspan={13}
                                align="center"
                                style={{ alignSelf: "center" }}
                              >
                                {" "}
                                <Loader speed="fast" />{" "}
                              </FlexboxGrid.Item>
                            </>
                          ) : (
                            displayRecentEDUValues
                              .filter((edu) => edu.id === system.id)
                              .map((data) => (
                                <>
                                  <FlexboxGrid.Item
                                    colspan={2}
                                    align="center"
                                    style={{ alignSelf: "center" }}
                                  >
                                    {data.temp_probe_0_value?.includes(
                                      "-196",
                                    ) ? (
                                      <AlertText severity="off">
                                        OFFLINE
                                      </AlertText>
                                    ) : data.temp_probe_1_value ? (
                                      `${data.temp_probe_0_value} ${data.temp_probe_0_units}`
                                    ) : null}
                                  </FlexboxGrid.Item>
                                  <FlexboxGrid.Item
                                    colspan={2}
                                    align="center"
                                    style={{ alignSelf: "center" }}
                                  >
                                    {data.temp_probe_1_value?.includes(
                                      "-196",
                                    ) ? (
                                      <AlertText severity="off">
                                        OFFLINE
                                      </AlertText>
                                    ) : data.temp_probe_1_value ? (
                                      `${data.temp_probe_1_value} ${data.temp_probe_1_units}`
                                    ) : null}
                                  </FlexboxGrid.Item>
                                  <FlexboxGrid.Item
                                    colspan={2}
                                    align="center"
                                    style={{ alignSelf: "center" }}
                                  >
                                    {data.room_temp_value
                                      ? `${data.room_temp_value} ${data.room_temp_units}`
                                      : null}
                                  </FlexboxGrid.Item>
                                  <FlexboxGrid.Item
                                    colspan={2}
                                    align="center"
                                    style={{ alignSelf: "center" }}
                                  >
                                    {data.room_humidity_value
                                      ? `${data.room_humidity_value} ${data.room_humidity_units}`
                                      : null}
                                  </FlexboxGrid.Item>
                                  <FlexboxGrid.Item
                                    colspan={2}
                                    align="center"
                                    style={{ alignSelf: "center" }}
                                  >
                                    {data.comp_vib_status === null ? (
                                      ``
                                    ) : data.comp_vib_status === true ? (
                                      <AlertText>ON</AlertText>
                                    ) : (
                                      <AlertText severity="high">OFF</AlertText>
                                    )}
                                  </FlexboxGrid.Item>
                                  <FlexboxGrid.Item
                                    colspan={3}
                                    style={{ alignSelf: "center" }}
                                  >
                                    {Interval.fromDateTimes(
                                      DateTime.fromISO(data?.edu_datetime),
                                      DateTime.now(),
                                    ).length("hours") > 6 ? (
                                      <DateTimeBox color={"red"}>
                                        {DateTime.fromISO(
                                          data?.edu_datetime,
                                        ).toFormat("ff")}
                                      </DateTimeBox>
                                    ) : (
                                      <DateTimeBox>
                                        {DateTime.fromISO(
                                          data?.edu_datetime,
                                        ).toFormat("ff")}
                                      </DateTimeBox>
                                    )}
                                  </FlexboxGrid.Item>
                                </>
                              ))
                          )}
                        </FlexboxGrid>
                      </SystemRow>
                    </List.Item>
                  )),
                ]);
              })()}
            </CustomerCard>
          ))}
        </List>
      ) : (
        <List size="sm" hover>
          {/* Header For Equipment List */}
          {displayCustomers.map((customer, index) => (
            <CustomerCard
              key={`customer_` + index}
              darkMode={darkMode}
              style={{ marginTop: index === 0 ? "16px" : undefined }}
            >
              <FlexboxGrid
                justify="center"
                style={{
                  paddingTop: 6,
                  paddingBottom: 6,
                  marginTop: -8,
                  marginLeft: -8,
                  marginRight: -8,
                  marginBottom: 3,
                  background: `${darkMode ? "#ffffff" : "#005b94"}`,
                  backdropFilter: "blur(10px)",
                  WebkitBackdropFilter: "blur(10px)",
                  border: `1px solid ${darkMode ? "rgba(255, 255, 255, 0.2)" : "rgba(0, 91, 148, 0.2)"}`,
                  borderRadius: "8px 8px 0 0",
                  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                }}
              >
                <FlexboxGrid.Item colspan={24} style={customerStyle}>
                  <CustomerText> {customer} </CustomerText>
                </FlexboxGrid.Item>
              </FlexboxGrid>

              {getGroupedSystemsForCustomer(customer).map(
                (siteGroupInfo, groupIndex) => (
                  <div key={`${customer}-${siteGroupInfo.site_group_name}`}>
                    {siteGroupInfo.is_group && (
                      <>
                        {/* Site Group Header */}
                        <FlexboxGrid
                          justify="center"
                          style={{
                            paddingTop: 3,
                            paddingBottom: 3,
                            marginTop: 8,
                            marginBottom: 2,
                            backgroundColor: `${darkMode ? appTheme.dark_accent : appTheme.avante_dark_blue}`,
                            border: `2px solid ${darkMode ? appTheme.dark_primary : appTheme.light_primary}`,
                            borderLeft: `4px solid ${darkMode ? "rgba(255, 255, 255, 0.4)" : "rgba(0, 51, 102, 0.4)"}`,
                            borderRadius: "0px",
                            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                          }}
                        >
                          <FlexboxGrid.Item colspan={24} style={siteGroupStyle}>
                            <SiteGroupText darkMode={darkMode}>
                              📁 {siteGroupInfo.site_group_name} (
                              {siteGroupInfo.sites.length})
                            </SiteGroupText>
                          </FlexboxGrid.Item>
                        </FlexboxGrid>

                        {/* Sites under this group */}
                        {siteGroupInfo.sites.map((siteName, siteIndex) => (
                          <FlexboxGrid
                            key={`${customer}-${siteGroupInfo.site_group_name}-${siteName}`}
                            justify="center"
                            style={{
                              paddingTop: 0.5,
                              paddingBottom: 0.5,
                              backgroundColor: `${darkMode ? "#2a2a2a" : "#f8f8f8"}`,
                              opacity: "70%",
                            }}
                          >
                            <FlexboxGrid.Item
                              colspan={24}
                              style={siteSubheadingStyle}
                            >
                              <SiteButton
                                onClick={() =>
                                  handleSiteClick(siteName, customer)
                                }
                              >
                                {" "}
                                {siteName}{" "}
                              </SiteButton>
                            </FlexboxGrid.Item>
                          </FlexboxGrid>
                        ))}
                      </>
                    )}

                    {!siteGroupInfo.is_group && (
                      <>
                        {/* Individual Sites Header */}
                        <FlexboxGrid
                          justify="center"
                          style={{
                            paddingTop: 3,
                            paddingBottom: 3,
                            marginTop: 8,
                            marginBottom: 2,
                            backgroundColor: `${darkMode ? "#4a4a4a" : "#6c757d"}`,
                            border: `2px solid ${darkMode ? appTheme.dark_primary : appTheme.light_primary}`,
                            borderLeft: `4px solid ${darkMode ? "rgba(255, 255, 255, 0.4)" : "rgba(0, 51, 102, 0.4)"}`,
                            borderRadius: "0px",
                            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                          }}
                        >
                          <FlexboxGrid.Item colspan={24} style={siteGroupStyle}>
                            <SiteGroupText darkMode={darkMode}>
                              Individual Sites ({siteGroupInfo.sites.length})
                            </SiteGroupText>
                          </FlexboxGrid.Item>
                        </FlexboxGrid>
                        {/* Sites under individual access */}
                        {siteGroupInfo.sites.map((siteName, siteIndex) => (
                          <FlexboxGrid
                            key={`${customer}-individual-${siteName}`}
                            justify="center"
                            style={{
                              paddingTop: 0.5,
                              paddingBottom: 0.5,
                              backgroundColor: `${darkMode ? "#2a2a2a" : "#f8f8f8"}`,
                              opacity: "70%",
                            }}
                          >
                            <FlexboxGrid.Item
                              colspan={24}
                              style={siteSubheadingStyle}
                            >
                              <SiteButton
                                onClick={() =>
                                  handleSiteClick(siteName, customer)
                                }
                              >
                                {" "}
                                {siteName}{" "}
                              </SiteButton>
                            </FlexboxGrid.Item>
                          </FlexboxGrid>
                        ))}
                      </>
                    )}
                  </div>
                ),
              )}

              {(() => {
                const groupedSystems = getGroupedSystemsForCustomer(customer);
                const hasGroups = groupedSystems.some(
                  (group) => group.type === "group",
                );

                return groupedSystems.flatMap((group, groupIndex) => [
                  // Add header for site groups and individual sites (only show Individual Sites header if there are also groups)
                  group.type === "group" ||
                  (group.type === "individual" && hasGroups) ? (
                    <FlexboxGrid
                      key={`${customer}-${group.type}-${group.site_group_name || "individual"}-mobile`}
                      justify="center"
                      style={{
                        paddingTop: 2,
                        paddingBottom: 0,
                        marginTop: 6,
                        marginLeft: -8,
                        marginRight: -8,
                        marginBottom: 0,
                        background: `${darkMode ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 91, 148, 0.08)"}`,
                        backdropFilter: "blur(10px)",
                        WebkitBackdropFilter: "blur(10px)",
                        border: `1px solid ${darkMode ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 91, 148, 0.1)"}`,
                        borderLeft: `4px solid ${darkMode ? "rgba(255, 255, 255, 0.4)" : "rgba(0, 51, 102, 0.4)"}`,
                        borderRadius: "0px",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                      }}
                    >
                      <FlexboxGrid.Item colspan={24} style={siteGroupStyle}>
                        <SiteGroupText darkMode={darkMode}>
                          {group.type === "group"
                            ? `${group.site_group_name} (${group.systems.length})`
                            : `Individual Sites (${group.systems.length})`}
                        </SiteGroupText>
                      </FlexboxGrid.Item>
                    </FlexboxGrid>
                  ) : null,
                  // Add all systems in this group
                  ...group.systems.map((system) => (
                    <List.Item
                      key={system.id + "_edu_mobile"}
                      style={{ height: "50px", fontSize: "11px" }}
                    >
                      <FlexboxGrid>
                        <FlexboxGrid.Item
                          colspan={4}
                          style={{ alignSelf: "center" }}
                        >
                          <ItemMainText>{system.id}</ItemMainText>
                          <ItemSubText>{system.cus_sys_id}</ItemSubText>
                        </FlexboxGrid.Item>

                        {loadingDisplayRecentEDUValues ? (
                          <>
                            <FlexboxGrid.Item
                              colspan={20}
                              align="center"
                              style={{ alignSelf: "center" }}
                            >
                              {" "}
                              <Loader speed="fast" />{" "}
                            </FlexboxGrid.Item>
                          </>
                        ) : (
                          displayRecentEDUValues
                            .filter((edu) => edu.id === system.id)
                            .map((data) => (
                              <>
                                <FlexboxGrid.Item
                                  colspan={3}
                                  align="center"
                                  style={{ alignSelf: "center" }}
                                >
                                  {data.temp_probe_0_value?.includes("-196") ? (
                                    <AlertText severity="off">
                                      OFFLINE
                                    </AlertText>
                                  ) : data.temp_probe_1_value ? (
                                    `${data.temp_probe_0_value} ${data.temp_probe_0_units}`
                                  ) : null}
                                </FlexboxGrid.Item>
                                <FlexboxGrid.Item
                                  colspan={3}
                                  align="center"
                                  style={{ alignSelf: "center" }}
                                >
                                  {data.temp_probe_1_value?.includes("-196") ? (
                                    <AlertText severity="off">
                                      OFFLINE
                                    </AlertText>
                                  ) : data.temp_probe_1_value ? (
                                    `${data.temp_probe_1_value} ${data.temp_probe_1_units}`
                                  ) : null}
                                </FlexboxGrid.Item>
                                <FlexboxGrid.Item
                                  colspan={3}
                                  align="center"
                                  style={{ alignSelf: "center" }}
                                >
                                  {data.room_temp_value
                                    ? `${data.room_temp_value} ${data.room_temp_units}`
                                    : null}
                                </FlexboxGrid.Item>
                                <FlexboxGrid.Item
                                  colspan={3}
                                  align="center"
                                  style={{ alignSelf: "center" }}
                                >
                                  {data.room_humidity_value
                                    ? `${data.room_humidity_value} ${data.room_humidity_units}`
                                    : null}
                                </FlexboxGrid.Item>
                                <FlexboxGrid.Item
                                  colspan={3}
                                  align="center"
                                  style={{ alignSelf: "center" }}
                                >
                                  {data.comp_vib_status === null ? (
                                    ``
                                  ) : data.comp_vib_status === true ? (
                                    <AlertText>ON</AlertText>
                                  ) : (
                                    <AlertText severity="high">OFF</AlertText>
                                  )}
                                </FlexboxGrid.Item>
                                <FlexboxGrid.Item
                                  colspan={5}
                                  style={{ alignSelf: "center" }}
                                >
                                  {Interval.fromDateTimes(
                                    DateTime.fromISO(data?.edu_datetime),
                                    DateTime.now(),
                                  ).length("hours") > 6 ? (
                                    <DateTimeBox color={"red"}>
                                      {DateTime.fromISO(
                                        data?.edu_datetime,
                                      ).toFormat("LLL dd - hh:mm a")}
                                    </DateTimeBox>
                                  ) : (
                                    <DateTimeBox>
                                      {DateTime.fromISO(
                                        data?.edu_datetime,
                                      ).toFormat("LLL dd - hh:mm a")}
                                    </DateTimeBox>
                                  )}
                                </FlexboxGrid.Item>
                              </>
                            ))
                        )}
                      </FlexboxGrid>
                    </List.Item>
                  )),
                ]);
              })()}
            </CustomerCard>
          ))}
        </List>
      )}
    </MachineTableSC>
  ) : (
    <></>
  );
};

const MachineTableSC = styled.div`
  width: 96vw;
  color: ${({ theme, darkMode }) =>
    darkMode ? theme.dark_accent : theme.light_accent};

  .rs-list-item {
    background-color: ${({ theme, darkMode }) =>
      darkMode ? theme.dark_primary : theme.light_primary};
  }

  padding-bottom: 50px;

  @media screen and (min-width: 769px) {
    width: 94vw;
  }
`;

const AlertText = styled.div`
  color: ${({ severity }) =>
    severity === "high"
      ? "#fff;"
      : severity === "medium"
        ? "#fda005"
        : severity === "low"
          ? "darkgrey"
          : severity === "off"
            ? "white"
            : "#2e7d32"};
  background-color: ${({ severity }) =>
    severity === "high"
      ? "#f44336;"
      : severity === "medium"
        ? "#ffeecc"
        : severity === "low"
          ? "#e6e5e3"
          : severity === "off"
            ? "lightgrey"
            : "#eeffed"};
  width: 50%;
  padding: 1%;
  text-align: center;
  border-radius: 6px;
  display: inline-block;
  line-height: 20px;
  font-size: 12px;
`;

const VersionText = styled.div`
  align-self: center;
  font-weight: bolder;
  color: rgb(0, 91, 148);
  background-color: lightgrey;
  text-align: center;
  border-radius: 6px;
  line-height: 20px;
`;

const ItemMainText = styled.div`
  font-size: 0.9em;
  font-weight: 500;
  text-overflow: ellipsis;
  line-height: 1.3;

  @media screen and (max-width: 769px) {
    font-size: 0.9em;
    font-weight: bolder;
  }
`;

const ItemSubText = styled.div`
  font-size: 0.7em;
  color: #97969b;
  font-weight: normal;
  line-height: 1.2;

  @media screen and (max-width: 769px) {
    font-size: 0.8em;
    font-weight: normal;
  }
`;

const HeaderText = styled.div`
  font-size: 0.8em;
  color: #97969b;
`;

const headerStyle = {
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
};

const RowHeader = () =>
  window.screen.width >= 768 ? (
    <FlexboxGrid justify="right" style={{ paddingTop: 5, paddingBottom: 5 }}>
      <FlexboxGrid.Item colspan={1} style={headerStyle} />
      <FlexboxGrid.Item colspan={2} style={headerStyle}>
        <HeaderText> EquipmentID </HeaderText>
      </FlexboxGrid.Item>
      <FlexboxGrid.Item colspan={3} style={headerStyle}>
        <HeaderText> Manufacturer </HeaderText>
      </FlexboxGrid.Item>
      <FlexboxGrid.Item colspan={4} style={headerStyle}>
        <HeaderText> Location </HeaderText>
      </FlexboxGrid.Item>
      <FlexboxGrid.Item colspan={1} style={headerStyle} align="center">
        <HeaderText> Alerts </HeaderText>
      </FlexboxGrid.Item>
      <FlexboxGrid.Item colspan={2} style={headerStyle} align="center">
        <HeaderText> Probe #1 </HeaderText>
      </FlexboxGrid.Item>
      <FlexboxGrid.Item colspan={2} style={headerStyle} align="center">
        <HeaderText> Probe #2 </HeaderText>
      </FlexboxGrid.Item>
      <FlexboxGrid.Item colspan={2} style={headerStyle} align="center">
        <HeaderText> Room Temp </HeaderText>
      </FlexboxGrid.Item>
      <FlexboxGrid.Item colspan={2} style={headerStyle} align="center">
        <HeaderText> Room Humidity </HeaderText>
      </FlexboxGrid.Item>
      <FlexboxGrid.Item colspan={2} style={headerStyle} align="center">
        <HeaderText> Compressor </HeaderText>
      </FlexboxGrid.Item>
      <FlexboxGrid.Item colspan={3} style={headerStyle}>
        <HeaderText> Date </HeaderText>
      </FlexboxGrid.Item>
    </FlexboxGrid>
  ) : (
    <FlexboxGrid justify="right" style={{ paddingTop: 5, paddingBottom: 5 }}>
      <FlexboxGrid.Item colspan={4} style={headerStyle}>
        <HeaderText> System ID </HeaderText>
      </FlexboxGrid.Item>
      <FlexboxGrid.Item colspan={3} style={headerStyle} align="center">
        <HeaderText>
          {" "}
          Probe <br />
          #1{" "}
        </HeaderText>
      </FlexboxGrid.Item>
      <FlexboxGrid.Item colspan={3} style={headerStyle} align="center">
        <HeaderText>
          {" "}
          Probe <br />
          #2{" "}
        </HeaderText>
      </FlexboxGrid.Item>
      <FlexboxGrid.Item colspan={3} style={headerStyle} align="center">
        <HeaderText> Room Temp </HeaderText>
      </FlexboxGrid.Item>
      <FlexboxGrid.Item colspan={3} style={headerStyle} align="center">
        <HeaderText> Room Humidity </HeaderText>
      </FlexboxGrid.Item>
      <FlexboxGrid.Item colspan={3} style={headerStyle} align="center">
        <HeaderText> Comp </HeaderText>
      </FlexboxGrid.Item>
      <FlexboxGrid.Item colspan={5} style={headerStyle} align="center">
        <HeaderText> Date </HeaderText>
      </FlexboxGrid.Item>
    </FlexboxGrid>
  );

const DateTimeBox = styled.div`
  color: ${({ color }) => (color ? color : "")};
  display: block;
  padding-right: 1px;

  @media screen and (max-width: 769px) {
    font-size: 0.8em;
  }
`;

const customerStyle = {
  display: "flex",
  paddingRight: "1%",
  flexDirection: "column",
  alignItems: "center",
  overflow: "hidden",
  fontWeight: "bold",
  position: "sticky",
  top: "0",
};

const siteGroupStyle = {
  display: "flex",
  paddingRight: "1%",
  flexDirection: "column",
  alignItems: "flex-start",
  overflow: "hidden",
  fontWeight: "600",
};

const siteSubheadingStyle = {
  display: "flex",
  paddingRight: "1%",
  flexDirection: "column",
  alignItems: "flex-start",
  overflow: "hidden",
  fontWeight: "normal",
};

const CustomerText = styled.div`
  font-size: 1em;
  font-weight: 700;
  color: #ffffff;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
  text-transform: uppercase;
  letter-spacing: 1px;

  @media screen and (max-width: 769px) {
    font-size: 0.85em;
    letter-spacing: 0.5px;
  }
`;

const SiteGroupText = styled.div`
  font-size: 0.8em;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.8px;

  @media screen and (max-width: 769px) {
    font-size: 0.75em;
    letter-spacing: 0.4px;
  }
`;

const SiteText = styled.div`
  padding-left: 1.5%;
  font-size: 0.7em;
  color: #97969b;
  font-weight: normal;

  @media screen and (max-width: 769px) {
    font-size: 0.6em;
  }
`;

const SiteButton = styled.button`
  padding-left: 1.5%;
  font-size: 0.7em;
  color: #4a90e2;
  font-weight: normal;
  background: none;
  border: none;
  cursor: pointer;
  text-decoration: underline;
  text-align: left;

  &:hover {
    color: #357abd;
    text-decoration: none;
  }

  @media screen and (max-width: 769px) {
    font-size: 0.6em;
  }
`;

const CustomerCard = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  margin-bottom: 16px;
  padding: 12px;
  box-shadow: ${shadows.sm};
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  transition: all 0.3s ease;

  @media screen and (max-width: 769px) {
    padding: 12px;
    margin-bottom: 16px;
  }
`;

const SystemRow = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  margin-bottom: 4px;
  padding: 8px 12px;
  transition: all 0.3s ease;
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
    transform: translateY(-1px);
    box-shadow: ${shadows.md};
  }

  &:last-child {
    margin-bottom: 0;
  }

  @media screen and (max-width: 769px) {
    padding: 16px 12px;
    margin-bottom: 8px;
  }
`;

const SiteGroupHeader = styled.div`
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-left: 3px solid ${colors.primary.main};
  border-radius: 8px;
  margin: 6px -12px 8px -12px;
  padding: 8px 12px;
  box-shadow: ${shadows.sm};
`;

const CustomerHeader = styled.div`
  background: ${colors.primary.main};
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px 12px 0 0;
  margin: -12px -12px 12px -12px;
  padding: 10px 16px;
  box-shadow: ${shadows.sm};
  display: flex;
  justify-content: center;
  align-items: center;
`;
